import {
  type Disposition,
  DISPOSITIONS,
  type FindingStatement,
  type PersistedFinding,
  SLOT_REVIEWER_EDITABLE,
  type SlotEdit,
  SlotEditSchema,
  type SlotKind,
  slotOf,
  type Treatment,
  TREATMENTS,
  withAuthoredSlots,
} from '@zevik/schemas';

/**
 * A schema refusal, read structurally: this app depends on `@zevik/schemas`
 * and not on `zod` (`client.test.ts` pins the dependency list), so the
 * error class is not importable here and `issues` is the shape read.
 */
const schemaIssues = (error: unknown): { message: string }[] | null =>
  typeof error === 'object' && error !== null && 'issues' in error && Array.isArray(error.issues)
    ? (error.issues as { message: string }[])
    : null;

/**
 * What a reviewer may say about a finding, and what the workspace sends
 * (docs/DECISIONS.md ZD-112).
 *
 * ## Two axes, never one
 *
 * A reviewer answers *is this true of the client's system* — `accept`, `reject`,
 * `defer` — and, only where the answer is yes, *what happens about it* —
 * `remediate` or `accepted_risk` (ZD-061). The finding's state is **derived** from
 * that pair and is never chosen, so this file offers no state picker: there is no
 * control for `approved` or `accepted_risk` as such, because those are what an
 * answer becomes.
 *
 * ## The affordance table, and why it is data rather than markup
 *
 * `ApproverDecisionSchema` refuses five combinations: a reject or defer with no
 * reason, a non-accept carrying a treatment, an accept carrying none, a
 * non-accept carrying a severity dissent, and a reject carrying an edit. A UI
 * that enforced those in JSX would be a **second expression of the same rules**,
 * in a representation nothing can compare to the first — CLAUDE.md's *two
 * expressions of one rule cannot be compared, so nothing can see them disagree*.
 *
 * So they are declared here as data, and `decision.test.ts` builds a decision for
 * **every cell in both directions** — every combination this table permits parses,
 * and every combination it forbids is refused — against the real schema, in a
 * package that can see both. The table is still a copy; what makes it safe is
 * that a comparator exists and runs, which is the difference between two rules
 * and two copies of one.
 *
 * ## An edit is offered, since `sds-12o`, and its rule is the schema's
 *
 * Which slots a reviewer may rewrite is `SLOT_REVIEWER_EDITABLE`, read rather
 * than restated; what a rewrite may say is `SlotEditSchema`'s refinement — no
 * legal conclusion, one paragraph — run here as a pre-flight so the sentence a
 * reviewer reads is the API's own, the way the password rule is run in the
 * browser (ZD-118 §3). Until `sds-12o` there was no edit control, and the
 * reason was on screen: an edit was accepted by the API and reached nothing.
 * It reaches the finding, the console and the report now (ZD-125 §1).
 */

/** What a disposition requires, permits and forbids. */
export interface Affordance {
  /** Prose for the reviewer, in the two-axis vocabulary rather than in state names. */
  label: string;
  /** Whether `treatment` must be chosen, may not be, and which values are offered. */
  treatment: 'required' | 'forbidden';
  /** Whether a written reason is required. It is always *permitted*. */
  reason: 'required' | 'optional';
  /** Whether a severity dissent may be recorded. */
  dissent: 'permitted' | 'forbidden';
}

export const AFFORDANCES: Readonly<Record<Disposition, Affordance>> = {
  accept: {
    label: 'This is true of their system',
    treatment: 'required',
    reason: 'optional',
    dissent: 'permitted',
  },
  reject: {
    label: 'This is not true of their system',
    treatment: 'forbidden',
    reason: 'required',
    dissent: 'forbidden',
  },
  defer: {
    label: 'I cannot tell without asking the client',
    treatment: 'forbidden',
    reason: 'required',
    dissent: 'forbidden',
  },
};

/** What each treatment means, said as a consequence rather than as a label. */
export const TREATMENT_LABELS: Readonly<Record<Treatment, string>> = {
  remediate: 'Remediate it',
  accepted_risk: 'Accept the risk',
};

/**
 * The slots a reviewer may rewrite, in the order a finding reads — read off
 * the schema's table, so a slot the table opens or closes moves here without
 * this file changing. `threat_action`'s long text is what a finding shows as
 * its description, and `grounds`' authored text stands in for the rationale.
 */
export const EDITABLE_SLOTS: readonly SlotKind[] = (
  Object.entries(SLOT_REVIEWER_EDITABLE) as [SlotKind, boolean][]
)
  .filter(([, editable]) => editable)
  .map(([slot]) => slot);

/** What each editable slot is called in front of a reviewer. */
export const EDIT_LABELS: Readonly<Partial<Record<SlotKind, string>>> = {
  threat_action: 'Rewrite the description',
  grounds: 'Rewrite the rationale',
};

/**
 * The slots a reviewer may rewrite **on this finding**: the editable ones
 * whose state is `established`. An absence may not acquire a sentence
 * (`FindingStatementSchema`, ZD-063 §2) — `grounds` is `not_applicable` on
 * 62 of the corpus's 102 findings, every undecided verdict — so a field for
 * it there would take a sentence the record refuses. The statement's own
 * schema is the rule; this reads the state it reads.
 */
export function editableSlotsFor(statement: FindingStatement): SlotKind[] {
  return EDITABLE_SLOTS.filter((slot) => slotOf(statement, slot).state === 'established');
}

/** What a reviewer has entered, before it becomes a decision. */
export interface DecisionDraft {
  disposition: Disposition;
  treatment: Treatment | null;
  reason: string;
  dissent: { band: string; reason: string } | null;
  /** A rewrite per slot the reviewer typed into; an empty string is no edit. */
  edits: Partial<Record<SlotKind, string>>;
}

/**
 * The ten fields `POST /v1/assessments/:id/decisions` takes for one finding.
 *
 * Named against `DecisionInput` in `apps/api`, which this app may not import (a
 * dependency on an app is not available to it), so the shape is restated and the
 * live route test is what holds the two together over HTTP.
 */
export interface DecisionRequest {
  finding_key: string;
  finding_structural_key: string;
  finding_id: string;
  pack_id: string;
  pack_version: string;
  disposition: Disposition;
  treatment: Treatment | null;
  reason: string | null;
  edits: SlotEdit[];
  severity_dissent: { band: string; reason: string } | null;
}

/** The edits a draft carries, trimmed, with empty fields dropped — not yet checked. */
export function draftEdits(draft: DecisionDraft): { slot: SlotKind; text: string }[] {
  return EDITABLE_SLOTS.flatMap((slot) => {
    const text = (draft.edits[slot] ?? '').trim();
    return text.length === 0 ? [] : [{ slot, text }];
  });
}

/**
 * Why a draft is not yet sendable, in the reviewer's words. Empty means send it.
 *
 * With the finding's statement, a rewrite is also laid on it the way the API
 * will lay it (`withAuthoredSlots`), so a sentence the statement's schema
 * refuses — a rewrite of a slot nothing established — is refused here in the
 * schema's words rather than stored and refused at every later read.
 */
export function draftProblems(draft: DecisionDraft, statement?: FindingStatement): string[] {
  const affordance = AFFORDANCES[draft.disposition];
  const problems: string[] = [];

  if (affordance.reason === 'required' && draft.reason.trim().length === 0) {
    problems.push(
      draft.disposition === 'reject'
        ? 'Say why this is not true of their system. A rejected finding is kept rather than ' +
            'deleted, so that a later pack revision can re-read what was refused and why.'
        : 'Say what this is waiting on, so whoever reads the deferral knows what would settle it. ' +
            'Note that the assessment cannot be approved while this stays deferred, and the ' +
            'round that would settle it is not on this screen yet.',
    );
  }
  if (affordance.treatment === 'required' && draft.treatment === null) {
    problems.push('Say what happens about it.');
  }
  if (affordance.treatment === 'forbidden' && draft.treatment !== null) {
    problems.push('Only an accepted finding carries a treatment.');
  }
  if (affordance.dissent === 'forbidden' && draft.dissent !== null) {
    problems.push(
      'A severity dissent belongs to an accepted finding, whose band is being asserted.',
    );
  }
  if (draft.dissent !== null && draft.dissent.reason.trim().length === 0) {
    problems.push('Say why you would band it differently.');
  }
  const edits = draftEdits(draft);
  if (draft.disposition === 'reject' && edits.length > 0) {
    problems.push(
      'A rejected finding is never shown to a client, so a rewrite of it has no reader. Clear ' +
        'the rewrite, or decide differently.',
    );
  }
  const wellFormed: SlotEdit[] = [];
  for (const edit of edits) {
    // The schema's own sentence, so what the reviewer reads here is what the
    // API would answer (ZD-118 §3).
    const parsed = SlotEditSchema.safeParse(edit);
    if (parsed.success) {
      wellFormed.push(parsed.data);
      continue;
    }
    for (const issue of parsed.error.issues) {
      problems.push(`${EDIT_LABELS[edit.slot] ?? edit.slot}: ${issue.message}.`);
    }
  }
  if (statement !== undefined && wellFormed.length > 0) {
    try {
      withAuthoredSlots(statement, wellFormed, 'approver_edited');
    } catch (error) {
      const issues = schemaIssues(error);
      if (issues === null) throw error;
      for (const issue of issues) problems.push(`${issue.message}.`);
    }
  }
  return problems;
}

/**
 * A draft as the request body, or a throw naming what is wrong.
 *
 * **Refuses rather than repairs.** A builder that quietly dropped a treatment on
 * a reject would send something the API accepts a different reading of, and the
 * reviewer would never know their answer had been altered. `draftProblems` is
 * what a screen shows; this is the last gate before the wire.
 */
export function buildDecisionRequest(
  finding: Pick<PersistedFinding['finding'], 'finding_id' | 'pack_id' | 'pack_version'>,
  keys: { finding_key: string; finding_structural_key: string },
  draft: DecisionDraft,
  statement?: FindingStatement,
): DecisionRequest {
  const problems = draftProblems(draft, statement);
  if (problems.length > 0) {
    throw new Error(`this decision is not sendable: ${problems.join(' ')}`);
  }

  return {
    finding_key: keys.finding_key,
    finding_structural_key: keys.finding_structural_key,
    finding_id: finding.finding_id,
    pack_id: finding.pack_id,
    pack_version: finding.pack_version,
    disposition: draft.disposition,
    treatment: draft.treatment,
    reason: draft.reason.trim().length === 0 ? null : draft.reason.trim(),
    /*
     * Parsed through the schema a second time on the way out, so a rewrite the
     * pre-flight above somehow let through is refused here rather than sent.
     * Until `sds-12o` this was `edits: []` unconditionally, with a comment
     * recording why: nothing wrote `Slot.authored_text` from an edit, so the
     * API accepted a rewrite and no reader saw it (ZD-125 §1).
     */
    edits: draftEdits(draft).map((edit) => SlotEditSchema.parse(edit)),
    severity_dissent: draft.dissent,
  };
}

/** A fresh draft for a finding nobody has decided. */
export function emptyDraft(): DecisionDraft {
  return { disposition: 'accept', treatment: null, reason: '', dissent: null, edits: {} };
}

export { DISPOSITIONS, TREATMENTS };
